import { useState } from 'react';

import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { SortableTh } from '../../components/ui/SortableTh';
import { ApiError } from '../../lib/api-client';
import { useTableSort } from '../../lib/table-sort';
import type { BillableAdmission } from './billable-admission';
import { useIpdBills } from './ipd-daycare.queries';
import { currency, formatIpdDateTime } from './ipd-daycare.utils';
import type { IpdBillStatus } from './types';

const statusLabel = (status: IpdBillStatus) => status === 'due' ? 'Due' : status === 'cancelled' ? 'Cancelled' : 'Paid';

export const IpdAllBillList = ({ admission, onViewBill }: { admission: BillableAdmission; onViewBill: (billId: string) => void }) => {
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const query = useIpdBills(admission.id, sortBy, sortOrder);
  const bills = query.data ?? [];
  const tableSort = useTableSort(sortBy, sortOrder, (next) => {
    setSortBy(next.key);
    setSortOrder(next.direction);
  });
  if (query.isLoading) return <LoadingState />;
  if (query.isError) return <ErrorState title="Unable to load bills" message={query.error instanceof ApiError ? query.error.message : 'Please try again.'} action={{ label: 'Try again', onClick: () => void query.refetch() }} />;

  return (
    <>
      <header className="opd-bill-banner">
        <div className="opd-bill-banner-row"><span>All Bill — {admission.patientSnapshot.name}</span></div>
        <p>{admission.admissionLabel} · {bills.length} bill{bills.length === 1 ? '' : 's'}</p>
      </header>
      <section className="opd-bill-subsection">
        {bills.length === 0 ? (
          <EmptyState title="No bills yet" message="No bill has been created for this admission." />
        ) : (
          <div className="table-scroll">
            <table className="opd-bill-payment-table">
              <thead><tr><th>#</th><SortableTh control={tableSort} column="billId">Bill No</SortableTh><SortableTh control={tableSort} column="billDate">Date</SortableTh><SortableTh control={tableSort} column="grandTotal">Total</SortableTh><SortableTh control={tableSort} column="pay">Pay</SortableTh><SortableTh control={tableSort} column="due">Due</SortableTh><SortableTh control={tableSort} column="credit">Credit</SortableTh><SortableTh control={tableSort} column="refund">Refund</SortableTh><SortableTh control={tableSort} column="status">Status</SortableTh><th>Action</th></tr></thead>
              <tbody>
                {bills.map((bill, index) => (
                  <tr key={bill.id} className={index === 0 ? 'opd-current-row' : undefined}>
                    <td>{index + 1}</td>
                    <td>{bill.billNumber}</td>
                    <td>{formatIpdDateTime(bill.billDate)}</td>
                    <td>{currency(bill.grandTotal)}</td>
                    <td>{currency(bill.totalPayment)}</td>
                    <td>{currency(bill.dueAmount)}</td>
                    <td>{currency(bill.creditAmount)}</td>
                    <td>{currency(bill.totalRefunded)}</td>
                    <td><span className={`opd-badge ${bill.status}`}>{statusLabel(bill.status)}</span></td>
                    <td><button type="button" className="opd-row-action" onClick={() => onViewBill(bill.id)}>{index === 0 ? 'Current' : 'View'}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
};
